/**
 * Alerts Hooks
 *
 * React Query hooks for the unified alerts dashboard.
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { ApiClient } from '../services/api';

// Query key factory
export const alertsKeys = {
  all: ['alerts'],
  lists: () => [...alertsKeys.all, 'list'],
  list: (filters) => [...alertsKeys.lists(), filters],
  summary: () => [...alertsKeys.all, 'summary'],
  detail: (id) => [...alertsKeys.all, 'detail', id],
};

// Auto-refresh interval (30 seconds for alerts)
const REFETCH_INTERVAL = 30000;
const STALE_TIME = 30000;

/**
 * Service class for alerts API
 */
class AlertsService {
  constructor(getToken) {
    this.client = new ApiClient(getToken);
  }

  /**
   * List alerts with filters
   */
  async listAlerts(params = {}, options = {}) {
    const queryParams = new URLSearchParams();

    if (params.category) queryParams.set('category', params.category);
    if (params.source) queryParams.set('source', params.source);
    if (params.severity) queryParams.set('severity', params.severity);
    if (params.status) queryParams.set('status', params.status);
    if (params.applicantId) queryParams.set('applicant_id', params.applicantId);
    if (params.dateFrom) queryParams.set('date_from', params.dateFrom);
    if (params.dateTo) queryParams.set('date_to', params.dateTo);
    if (params.sortBy) queryParams.set('sort_by', params.sortBy);
    if (params.sortOrder) queryParams.set('sort_order', params.sortOrder);
    if (params.limit) queryParams.set('limit', params.limit);
    if (params.offset) queryParams.set('offset', params.offset);
    if (params.is_sandbox) queryParams.set('is_sandbox', 'true');

    const queryString = queryParams.toString();
    const url = queryString ? `/alerts?${queryString}` : '/alerts';

    return this.client.get(url, options);
  }

  /**
   * Get alert summary stats
   */
  async getSummary({ is_sandbox = false, ...options } = {}) {
    const query = is_sandbox ? '?is_sandbox=true' : '';
    return this.client.get(`/alerts/summary${query}`, options);
  }

  /**
   * Get single alert
   */
  async getAlert(id, options = {}) {
    return this.client.get(`/alerts/${id}`, options);
  }

  /**
   * Resolve an alert
   */
  async resolveAlert(id, data, options = {}) {
    return this.client.post(`/alerts/${id}/resolve`, data, options);
  }

  /**
   * Escalate an alert
   */
  async escalateAlert(id, data, options = {}) {
    return this.client.post(`/alerts/${id}/escalate`, data, options);
  }

  /**
   * Batch resolve alerts
   */
  async batchResolve(alertIds, data, options = {}) {
    return this.client.post('/alerts/batch-resolve', {
      alert_ids: alertIds,
      ...data,
    }, options);
  }

  /**
   * Batch escalate alerts
   */
  async batchEscalate(alertIds, data, options = {}) {
    return this.client.post('/alerts/batch-escalate', {
      alert_ids: alertIds,
      ...data,
    }, options);
  }

  /**
   * Import external alert
   */
  async importAlert(data, options = {}) {
    return this.client.post('/alerts/import', data, options);
  }
}

/**
 * Hook to list alerts with filters
 */
export function useAlerts(params = {}, options = {}) {
  const { getToken } = useAuth();
  const service = useMemo(() => new AlertsService(getToken), [getToken]);

  return useQuery({
    queryKey: alertsKeys.list(params),
    queryFn: ({ signal }) => service.listAlerts(params, { signal }),
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
    ...options,
  });
}

/**
 * Hook to get alert summary stats
 */
export function useAlertsSummary({ is_sandbox = false } = {}, options = {}) {
  const { getToken } = useAuth();
  const service = useMemo(() => new AlertsService(getToken), [getToken]);

  return useQuery({
    queryKey: [...alertsKeys.summary(), { is_sandbox }],
    queryFn: ({ signal }) => service.getSummary({ is_sandbox, signal }),
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
    ...options,
  });
}

/**
 * Hook to get single alert
 */
export function useAlert(id, options = {}) {
  const { getToken } = useAuth();
  const service = useMemo(() => new AlertsService(getToken), [getToken]);

  return useQuery({
    queryKey: alertsKeys.detail(id),
    queryFn: ({ signal }) => service.getAlert(id, { signal }),
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to resolve an alert
 */
export function useResolveAlert() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const service = useMemo(() => new AlertsService(getToken), [getToken]);

  return useMutation({
    mutationFn: ({ id, data }) => service.resolveAlert(id, data),
    onSuccess: () => {
      // Invalidate all alerts queries
      queryClient.invalidateQueries({ queryKey: alertsKeys.all });
    },
  });
}

/**
 * Hook to escalate an alert
 */
export function useEscalateAlert() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const service = useMemo(() => new AlertsService(getToken), [getToken]);

  return useMutation({
    mutationFn: ({ id, data }) => service.escalateAlert(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.all });
    },
  });
}

/**
 * Hook to batch resolve alerts
 */
export function useBatchResolveAlerts() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const service = useMemo(() => new AlertsService(getToken), [getToken]);

  return useMutation({
    mutationFn: ({ alertIds, data }) => service.batchResolve(alertIds, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.all });
    },
  });
}

/**
 * Hook to batch escalate alerts
 */
export function useBatchEscalateAlerts() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const service = useMemo(() => new AlertsService(getToken), [getToken]);

  return useMutation({
    mutationFn: ({ alertIds, data }) => service.batchEscalate(alertIds, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.all });
    },
  });
}

/**
 * Hook to import external alert
 */
export function useImportAlert() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const service = useMemo(() => new AlertsService(getToken), [getToken]);

  return useMutation({
    mutationFn: (data) => service.importAlert(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.all });
    },
  });
}

export default useAlerts;
